import Spinner from '@/components/Spinner'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Form, FormControl } from '@/components/ui/form'
import { FormMessage } from '@/components/ui/form'
import { FormItem } from '@/components/ui/form'
import { FormField } from '@/components/ui/form'
import { getPlaidLinkTokenGetPlaidLinkTokenGetOptions } from '@/generated/@tanstack/react-query.gen'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery } from '@tanstack/react-query'
import { get } from 'lodash'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { usePlaidLinkStore } from './payment-method-plaid-link'

interface Props {
  onOpenChange: () => void
}

const addACHSchema = z.object({
  authorizCheck: z
    .boolean()
    .refine(
      (val) => val === true,
      'You must agree to authorize Hydra Logic Inc. to automatically debit payments.'
    ),
  acceptTerms: z
    .boolean()
    .refine(
      (val) => val === true,
      'You must agree to the Terms & Conditions to continue.'
    ),
})

export type AddPaymentACHFormType = z.infer<typeof addACHSchema>

export default function PaymentMethodAddACH({ onOpenChange }: Props) {
  const { setLinkToken } = usePlaidLinkStore()

  const form = useForm<AddPaymentACHFormType>({
    resolver: zodResolver(addACHSchema),
    defaultValues: {
      authorizCheck: false,
      acceptTerms: false,
    },
  })

  const { data, isFetching, refetch } = useQuery({
    ...getPlaidLinkTokenGetPlaidLinkTokenGetOptions({}),
    enabled: false,
    select: (value) => get(value, 'link_token', ''),
    gcTime: 0,
    staleTime: 0,
  })

  useEffect(() => {
    if (data) {
      setLinkToken(data)
      onOpenChange()
    }
  }, [data])

  function onSubmit(data: AddPaymentACHFormType) {
    setLinkToken(undefined)
    refetch()
  }

  return (
    <div className="w-full py-8">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
          <div className="space-y-4">
            <FormField
              control={form.control}
              name="authorizCheck"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <div className="flex flex-row items-center gap-2">
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        id="authorize-debit"
                      />
                      <label
                        htmlFor="authorize-debit"
                        className="cursor-pointer"
                      >
                        I authorize Hydra Logic Inc. to automatically debit payments
                        from my connected bank account.
                      </label>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="acceptTerms"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <div className="flex flex-row items-center gap-2">
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        id="accept-terms"
                      />
                      <label htmlFor="accept-terms" className="cursor-pointer">
                        I have read and agree to{' '}
                        <a
                          href="https://hydrascore.com/terms-and-conditions"
                          className="text-primary hover:underline"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Hydra Logic Inc. Terms & Conditions
                        </a>
                        .
                      </label>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="flex gap-4">
            <Button
              disabled={false}
              className="h-14 min-w-[113px]"
              type="submit"
            >
              Connect Bank Account
            </Button>
            <Button
              disabled={false}
              className="h-14 min-w-[113px]"
              type="button"
              variant="ghost"
              onClick={() => onOpenChange()}
            >
              Cancel
            </Button>
          </div>
        </form>
      </Form>
    </div>
  )
}
